import { useEffect, useRef, useState } from "react";

import { useI18n } from "~/i18n";
import { cn } from "~/lib/utils";

/**
 * A title you rename by clicking it.
 *
 * Renaming used to mean opening the workflow, finding the name field, editing and saving — four
 * steps away from the list where you are looking at the wrong name. So the heading itself is the
 * control: click, type, done. It commits on blur and on Enter, because those are both "I have
 * finished typing", and abandons on Escape.
 *
 * Two things it deliberately does NOT do:
 *   - save on every keystroke. A rename is one edit, not twenty, and a request per character
 *     would race its own responses and leave whichever landed last on screen.
 *   - accept an empty name. A nameless row is unfindable in the list it was renamed from, so a
 *     blank reverts to what was there (the server refuses it too).
 *
 * The caller owns persistence and shows the error; this owns only the editing.
 */
export function InlineRename({
  value,
  onRename,
  className,
  inputClassName,
}: {
  value: string;
  /** Commit. Returning false puts the old name back — the caller reports why. */
  onRename: (next: string) => Promise<boolean>;
  className?: string;
  inputClassName?: string;
}) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // A commit can fire from blur AND from Enter (Enter blurs the field), so the second one must
  // not send a duplicate request.
  const committedRef = useRef(false);

  // A rename from elsewhere (a reload, another tab) should show while not editing.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (!editing) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [editing]);

  const commit = async () => {
    if (committedRef.current) return;
    committedRef.current = true;
    const next = draft.trim();
    if (!next || next === value) {
      setDraft(value);
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await onRename(next);
    setSaving(false);
    setEditing(false);
    if (!ok) setDraft(value);
  };

  if (!editing) {
    return (
      <button
        type="button"
        title={t("workflows.rename.hint")}
        onClick={(event) => {
          // The title usually sits inside a link to the item; editing it is not navigation.
          event.preventDefault();
          event.stopPropagation();
          committedRef.current = false;
          setEditing(true);
        }}
        className={cn(
          "max-w-full truncate rounded px-1 -mx-1 text-left transition-colors hover:bg-accent/30",
          className,
        )}
      >
        {value}
      </button>
    );
  }

  return (
    <input
      ref={inputRef}
      value={draft}
      disabled={saving}
      aria-label={t("workflows.rename.label")}
      onChange={(event) => setDraft(event.target.value)}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onBlur={() => void commit()}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          void commit();
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          // Mark committed so the blur that follows cannot save the abandoned draft.
          committedRef.current = true;
          setDraft(value);
          setEditing(false);
        }
      }}
      className={cn(
        "w-full min-w-0 rounded border border-primary/50 bg-background px-1 -mx-1 outline-none",
        saving && "opacity-60",
        inputClassName,
      )}
    />
  );
}
